import { Badge, Box, Button, Flex, Input, Text } from "@chakra-ui/react";
import React, { useState } from "react";
import { MdEdit, MdDeleteOutline } from "react-icons/md";
import { MEETING_REQUEST_STATUS, relevanceFields } from "../../Constants/data";
import { formatMeetingDate } from "./useMeetingListState";

// Claude: extracted from MeetingList.tsx 23-03-2026
interface MeetingCardProps {
  item: any;
  isRedirected: boolean;
  itemRef: React.RefObject<HTMLDivElement>;
  // current filter status — used to show status label when showing "All"
  status: string;
  meetsStatusArray: any[];
  todayDate: number;
  user: any;
  userMobileNumber: string;
  userTimezone: string;
  userPlan: any;
  meetings: any[];
  // action handlers
  allowCall: (date: string, time: string) => boolean;
  handleUpdate: (id: string, data: any) => void;
  handleOpenMeetConfirmModal: (id: string) => void;
  buttonTypeAcceptClick: (id: string) => void;
  setSelectedMeet: (m: any) => void;
  setResponder: (m: any) => void;
  handleCalendarModalOpen: () => void;
  setType: (t: "new" | "edit") => void;
  setSelectedChangeMeeting: (m: any) => void;
  handleOpenChangeConfirmationModal: () => void;
  openProfileDrawer: (item: any) => void;
  getRelevanceStatusForMeeting: (meeting: any) => string;
  handleRelevanceClick: (meeting: any) => void;
  isFeedbackVisible: (meeting: any) => boolean;
  handleFeedbackClick: (meeting: any) => void;
  error: (msg: string) => void;
  handleUpdateMeetingUrl: (id: string, url: string) => void; // Claude: N44 26-03-2026
}

// ─── Action Button Components ─────────────────────────────────────────────────

const ButtonTypeCancel = ({
  item, id, allowCall, error, handleOpenMeetConfirmModal,
}: {
  item: any; id: string;
  allowCall: (d: string, t: string) => boolean;
  error: (msg: string) => void;
  handleOpenMeetConfirmModal: (id: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"red.100"} color={"red.600"}
    onClick={() => {
      if (item && allowCall(item?.proposedDate, item?.proposedTime))
        return error("Call button is enabled. Meeting cannot be cancelled now.");
      handleOpenMeetConfirmModal(id);
    }}
  >
    Cancel
  </Button>
);

const ButtonTypeHold = ({
  id, handleUpdate,
}: {
  id: string;
  handleUpdate: (id: string, data: any) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"yellow.100"} color={"yellow.600"}
    onClick={() => handleUpdate(id, { response: MEETING_REQUEST_STATUS.PUT_ON_HOLD })}
  >
    Hold
  </Button>
);

const ButtonTypeNewRequest = ({
  id, userMobileNumber, userTimezone, meetings, setSelectedMeet, setResponder,
  handleCalendarModalOpen, setType, error,
}: {
  id: string;
  userMobileNumber: string; userTimezone: string;
  meetings: any[];
  setSelectedMeet: (m: any) => void; setResponder: (m: any) => void;
  handleCalendarModalOpen: () => void; setType: (t: "new" | "edit") => void;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"green.100"} color={"green.600"}
    onClick={() => {
      if (!userMobileNumber)
        return error("Please provide your contact number in the profile page");
      if (!userTimezone)
        return error("Please save your current timezone in the profile page");
      setSelectedMeet(meetings.find((meet: any) => meet._id == id));
      setResponder(meetings.find((meet: any) => meet._id == id));
      handleCalendarModalOpen();
      setType("new");
    }}
  >
    New Request
  </Button>
);

const ButtonTypeChange = ({
  id, item, userMobileNumber, userTimezone, userPlan, meetings,
  allowCall, setSelectedChangeMeeting, handleOpenChangeConfirmationModal, error,
}: {
  id: string; item: any;
  userMobileNumber: string; userTimezone: string; userPlan: any;
  meetings: any[];
  allowCall: (d: string, t: string) => boolean;
  setSelectedChangeMeeting: (m: any) => void;
  handleOpenChangeConfirmationModal: () => void;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"orange.100"} color={"orange.600"}
    onClick={() => {
      if (allowCall(item.proposedDate, item.proposedTime))
        return error("Call button is enabled. Meeting cannot be changed now.");
      if (!userMobileNumber)
        return error("Please provide your contact number in the profile page");
      if (!userTimezone)
        return error("Please save your current timezone in the profile page");
      setSelectedChangeMeeting(meetings.find((meet: any) => meet._id == id));
      handleOpenChangeConfirmationModal();
    }}
  >
    Change
  </Button>
);

const ButtonTypeProposeTime = ({
  id, userMobileNumber, userTimezone, meetings, setSelectedMeet,
  handleCalendarModalOpen, setType, error,
}: {
  id: string;
  userMobileNumber: string; userTimezone: string;
  meetings: any[];
  setSelectedMeet: (m: any) => void;
  handleCalendarModalOpen: () => void; setType: (t: "new" | "edit") => void;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"green.100"} color={"green.600"}
    onClick={() => {
      if (!userMobileNumber)
        return error("Please provide your contact number in the profile page");
      if (!userTimezone)
        return error("Please save your current timezone in the profile page");
      const meetToEdit = meetings.find((meet: any) => meet._id == id);
      if (meetToEdit) {
        setSelectedMeet(meetToEdit);
        handleCalendarModalOpen();
        setType("edit");
      } else {
        error("Unable to find meeting!");
      }
    }}
  >
    Propose Time
  </Button>
);

const ButtonTypeAccept = ({
  id, disabled, userMobileNumber, userTimezone, buttonTypeAcceptClick, error,
}: {
  id: string; disabled: boolean;
  userMobileNumber: string; userTimezone: string;
  buttonTypeAcceptClick: (id: string) => void;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"green.100"} color={"green.600"}
    onClick={() => {
      if (!userMobileNumber)
        return error("Please provide your contact number in the profile page");
      if (!userTimezone)
        return error("Please save your current timezone in the profile page");
      buttonTypeAcceptClick(id);
    }}
    isDisabled={disabled}
  >
    Accept
  </Button>
);

const ButtonTypeCall = ({
  item, user, allowCall, error,
}: {
  item: any; user: any;
  allowCall: (d: string, t: string) => boolean;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"blue.100"} color={"blue.600"}
    onClick={() => {
      if (!allowCall(item.proposedDate, item.proposedTime))
        return error("Call button will be enabled 10 minutes before the meeting and remain active for up to 1 hour.");
      if (user.role == "Seller") {
        // Seller calls the buyer — phone is on the buyer's (other-party's) profile
        const resMobile =
          user.user === item.responderDetails.userId
            ? item.requesterDetails.buyerPhoneNumber
            : item.responderDetails.buyerPhoneNumber;
        window.location.href = "tel:" + resMobile;
      } else if (user.role == "Buyer") {
        // Buyer calls the seller — phone is on the seller's (other-party's) profile
        const resMobile =
          user.user === item.requesterDetails.userId
            ? item.responderDetails.personPhoneNumber
            : item.requesterDetails.personPhoneNumber;
        window.location.href = "tel:" + resMobile;
      }
    }}
  >
    Call
  </Button>
);

// Claude: Join Meeting button — other party only, teal, same error-on-click pattern as Call 30-03-2026
const ButtonTypeJoin = ({
  item, allowCall, error,
}: {
  item: any;
  allowCall: (d: string, t: string) => boolean;
  error: (msg: string) => void;
}) => (
  <Button flex={1} size={"sm"} p={0} py={2} w={"100%"} mt={2} bg={"teal.100"} color={"teal.600"}
    onClick={() => {
      if (!allowCall(item.proposedDate, item.proposedTime))
        return error("Join button will be enabled 10 minutes before the meeting and remain active for up to 1 hour.");
      window.open(item.meetingURL, "_blank");
    }}
  >
    Join
  </Button>
);

// ─── Inline sub-components ────────────────────────────────────────────────────

// Coloured relevance square — click opens relevance modal for the other party
const RelevanceStatusIndicator = ({
  meeting, user, getRelevanceStatusForMeeting, handleRelevanceClick,
}: {
  meeting: any; user: any;
  getRelevanceStatusForMeeting: (m: any) => string;
  handleRelevanceClick: (m: any) => void;
}) => {
  const relevanceStatus = getRelevanceStatusForMeeting(meeting);
  const relevanceField = relevanceFields.find((s) => s.status === relevanceStatus);
  return (
    <>
      <Box
        height={18} width={18} borderRadius={"2px"}
        background={relevanceField?.color || "#94A3B8"}
        cursor="pointer"
        onClick={(e: any) => { e.stopPropagation(); handleRelevanceClick(meeting); }}
        title={`Click to change relevance status (${user.role === "Seller" ? "prequalifying buyer" : "prequalifying seller"})`}
      />
      {/* Claude: RFX context badge — shows when meeting originated from an RFX snippet 24-02-2026 */}
      {meeting.rfxId && (
        <Badge colorScheme="purple" textTransform="none" fontSize="10px" borderRadius="4px">RFX</Badge>
      )}
    </>
  );
};

// Feedback link — shown only to sellers within 24 hrs of meeting closure
const FeedbackLink = ({
  meeting, isFeedbackVisible, handleFeedbackClick,
}: {
  meeting: any;
  isFeedbackVisible: (m: any) => boolean;
  handleFeedbackClick: (m: any) => void;
}) => {
  if (!isFeedbackVisible(meeting)) return null;
  return (
    <Text
      fontSize={"12px"} color={"blue.600"} cursor={"pointer"} textDecoration={"underline"}
      onClick={(e: any) => { e.stopPropagation(); handleFeedbackClick(meeting); }}
      _hover={{ color: "blue.800" }}
      title="Click to provide feedback for this meeting"
    >
      Feedback (within 24 hrs of closure.)
    </Text>
  );
};

// ─── Card layout helpers ──────────────────────────────────────────────────────

// Top row: date on left, status label on right (only when showing "All")
const CardDateRow = ({
  item, status, meetsStatusArray, user,
}: {
  item: any; status: string; meetsStatusArray: any[]; user: any;
}) => (
  <Flex justifyContent={"space-between"} alignItems={"center"}>
    <Text fontSize={"xs"}>Date: {formatMeetingDate(item.meetingUTC)}</Text>
    {status == MEETING_REQUEST_STATUS.TOTAL && (
      <Text fontSize={"xs"}>
        {meetsStatusArray?.find(
          (s) =>
            s.status ==
            // My status: responder sees responderStatus; requester sees requesterStatus
            (user.user == item.responderId ? item.responderStatus : item.requesterStatus)
        )?.label}
      </Text>
    )}
  </Flex>
);

// Middle row: clickable company name + context badge + (optional feedback) + relevance square
const CardCompanyRow = ({
  item, user, openProfileDrawer,
  getRelevanceStatusForMeeting, handleRelevanceClick,
  isFeedbackVisible, handleFeedbackClick,
  showFeedback = false,
}: {
  item: any; user: any;
  openProfileDrawer: (item: any) => void;
  getRelevanceStatusForMeeting: (m: any) => string;
  handleRelevanceClick: (m: any) => void;
  isFeedbackVisible: (m: any) => boolean;
  handleFeedbackClick: (m: any) => void;
  showFeedback?: boolean;
}) => (
  <Flex alignItems={"center"} justifyContent={"space-between"} gap={2} mt={3}>
    <Flex alignItems={"center"} gap={2}>
      <Text
        fontSize={"sm"} fontWeight={600} color={"blue.600"}
        cursor={"pointer"} _hover={{ textDecoration: "underline" }}
        onClick={(e) => { e.stopPropagation(); openProfileDrawer(item); }}
      >
        {/* Other party's company: if I am the responder the other party is the requester */}
        {user.user == item.responderId ? item.requesterCompany : item.responderCompany}
      </Text>
      {/* Claude: context badge (RFX or Inquiry) next to company name 26-02-2026 */}
      {item.context && (
        <Badge colorScheme={item.context === "RFX" ? "purple" : "blue"} textTransform="none" fontSize="10px">
          {item.context}
        </Badge>
      )}
      {showFeedback && (
        <FeedbackLink
          meeting={item}
          isFeedbackVisible={isFeedbackVisible}
          handleFeedbackClick={handleFeedbackClick}
        />
      )}
    </Flex>
    <RelevanceStatusIndicator
      meeting={item}
      user={user}
      getRelevanceStatusForMeeting={getRelevanceStatusForMeeting}
      handleRelevanceClick={handleRelevanceClick}
    />
  </Flex>
);

// Claude: floating label input for meeting link — defined outside MeetingCard to prevent remount 30-03-2026
const MeetingLinkInput = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const [focused, setFocused] = useState(false);
  const isActive = focused || value.length > 0;
  return (
    <Box position="relative" w="100%" pt="14px">
      <Box position="absolute" left={0} top={isActive ? "0" : "22px"} transition="all 0.2s ease" zIndex={1} pointerEvents="none">
        <Text fontSize={isActive ? "xs" : "sm"} color={isActive ? "green.600" : "gray.400"}>
          Add Meeting Link URL (Meet/Zoom etc.)
        </Text>
      </Box>
      <Input
        variant="flushed"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        borderColor="gray.400"
        _focus={{ borderColor: "green.600", boxShadow: "none" }}
      />
    </Box>
  );
};

// ─── MeetingCard ──────────────────────────────────────────────────────────────

const MeetingCard = ({
  item, isRedirected, itemRef, status, meetsStatusArray, todayDate,
  user, userMobileNumber, userTimezone, userPlan, meetings,
  allowCall, handleUpdate, handleOpenMeetConfirmModal, buttonTypeAcceptClick,
  setSelectedMeet, setResponder, handleCalendarModalOpen, setType,
  setSelectedChangeMeeting, handleOpenChangeConfirmationModal,
  openProfileDrawer, getRelevanceStatusForMeeting, handleRelevanceClick,
  isFeedbackVisible, handleFeedbackClick, error, handleUpdateMeetingUrl,
}: MeetingCardProps) => {
  // Claude: N44 — local state for meeting URL edit mode 26-03-2026
  const [editingMeetingURL, setEditingMeetingURL] = useState(false);
  const [meetingURL, setMeetingURL] = useState('');
  const [confirmDeleteURL, setConfirmDeleteURL] = useState(false);

  // My status: responder sees responderStatus; requester sees requesterStatus
  const myStatus = user.user == item.responderId ? item.responderStatus : item.requesterStatus;

  // Deep-link highlight — only when user arrived via a ?id= link
  const bg = (deepLinkColor: string) =>
    item.latestMeeting && isRedirected ? deepLinkColor : "white";

  const cardRef = item.latestMeeting
    ? (itemRef as React.LegacyRef<HTMLDivElement>)
    : null;

  const sharedCardBox = {
    shadow: "md" as const,
    border: "1px solid" as const,
    borderColor: "gray.200",
    rounded: "lg",
    p: 3,
    ref: cardRef,
  };

  // Shared props for date/company rows
  const rowProps = {
    item, user, status, meetsStatusArray,
    openProfileDrawer, getRelevanceStatusForMeeting, handleRelevanceClick,
    isFeedbackVisible, handleFeedbackClick,
  };

  // Shared button props (all button components need a subset of these)
  const btnBase = {
    userMobileNumber, userTimezone, userPlan, meetings, allowCall,
    handleUpdate, handleOpenMeetConfirmModal,
    setSelectedMeet, setResponder, handleCalendarModalOpen, setType,
    setSelectedChangeMeeting, handleOpenChangeConfirmationModal, error,
  };

  const acceptDisabled =
    new Date(`${item.proposedDate}T${item.proposedTime}:00`).getTime() < todayDate;

  if (myStatus === MEETING_REQUEST_STATUS.CONFIRM) {
    const isOriginator = user.user == item.requesterId;
    return (
      <Box {...sharedCardBox} bg={bg("#E9FAF7")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
        {/* Claude: meeting URL section — originator: floating field + pencil/delete; other party: nothing (Join is in action row) 30-03-2026 */}
        {isOriginator && (
          editingMeetingURL ? (
            <Box mt={3}>
              <MeetingLinkInput value={meetingURL} onChange={setMeetingURL} />
              <Flex gap={2} mt={2}>
                <Button size="sm" bg="#2E7D32" color="white" _hover={{ bg: "#1B5E20" }} borderRadius="full" px={4}
                  onClick={() => { handleUpdateMeetingUrl(item._id, meetingURL); setEditingMeetingURL(false); }}>
                  Save
                </Button>
                <Button size="sm" variant="ghost" color="gray.400" borderRadius="full"
                  onClick={() => setEditingMeetingURL(false)}>
                  ✕
                </Button>
              </Flex>
            </Box>
          ) : item.meetingURL ? (
            <Flex mt={3} alignItems="center" gap={2} flexWrap="wrap">
              <Text fontSize="xs" color="blue.600" textDecoration="underline" cursor="pointer"
                onClick={() => window.open(item.meetingURL, "_blank")}
                noOfLines={1} maxW="180px">
                {item.meetingURL}
              </Text>
              <Box as={MdEdit} boxSize="14px" color="gray.500" cursor="pointer"
                onClick={() => { setMeetingURL(item.meetingURL); setEditingMeetingURL(true); setConfirmDeleteURL(false); }} />
              <Box as={MdDeleteOutline} boxSize="14px" color="red.400" cursor="pointer"
                onClick={() => setConfirmDeleteURL(true)} />
              {confirmDeleteURL && (
                <Flex alignItems="center" gap={1}>
                  <Text fontSize="xs" color="gray.600">Remove?</Text>
                  <Text fontSize="xs" color="red.500" cursor="pointer" fontWeight="bold"
                    onClick={() => { handleUpdateMeetingUrl(item._id, ''); setConfirmDeleteURL(false); }}>Yes</Text>
                  <Text fontSize="xs" color="gray.400" cursor="pointer"
                    onClick={() => setConfirmDeleteURL(false)}>No</Text>
                </Flex>
              )}
            </Flex>
          ) : (
            <Text mt={3} fontSize="xs" color="gray.400" cursor="pointer"
              onClick={() => { setMeetingURL(''); setEditingMeetingURL(true); }}>
              + Add Meeting Link
            </Text>
          )
        )}
        <Flex gap={4}>
          {!isOriginator && item.meetingURL && (
            <ButtonTypeJoin item={item} allowCall={allowCall} error={error} />
          )}
          <ButtonTypeCall item={item} user={user} allowCall={allowCall} error={error} />
          <ButtonTypeChange item={item} id={item._id} {...btnBase} />
          <ButtonTypeCancel item={item} id={item._id} allowCall={allowCall} error={error} handleOpenMeetConfirmModal={handleOpenMeetConfirmModal} />
        </Flex>
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.REQUEST_RECEIVED) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
        <Flex gap={4}>
          {!item.proposedDate || !item.proposedTime ? (
            <>
              <ButtonTypeProposeTime id={item._id} {...btnBase} />
              <ButtonTypeHold id={item._id} handleUpdate={handleUpdate} />
            </>
          ) : (
            <>
              <ButtonTypeAccept id={item._id} userMobileNumber={userMobileNumber} userTimezone={userTimezone}
                buttonTypeAcceptClick={buttonTypeAcceptClick} error={error} disabled={acceptDisabled} />
              <ButtonTypeChange item={item} id={item._id} {...btnBase} />
              <ButtonTypeHold id={item._id} handleUpdate={handleUpdate} />
            </>
          )}
        </Flex>
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.RESPONSE_AWAITED) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
        <Flex gap={4}>
          <ButtonTypeCancel item={item} id={item._id} allowCall={allowCall} error={error} handleOpenMeetConfirmModal={handleOpenMeetConfirmModal} />
        </Flex>
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.PUT_ON_HOLD) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
        <Flex gap={4}>
          {!item.proposedDate || !item.proposedTime ? (
            <>
              <ButtonTypeProposeTime id={item._id} {...btnBase} />
              <ButtonTypeChange item={item} id={item._id} {...btnBase} />
            </>
          ) : (
            <>
              <ButtonTypeAccept id={item._id} userMobileNumber={userMobileNumber} userTimezone={userTimezone}
                buttonTypeAcceptClick={buttonTypeAcceptClick} error={error} disabled={acceptDisabled} />
              <ButtonTypeChange item={item} id={item._id} {...btnBase} />
            </>
          )}
        </Flex>
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.EXPIRED) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.CANCELLED) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.CLOSED) {
    return (
      <Box {...sharedCardBox} bg={bg("#F7DBB4")}>
        <CardDateRow {...rowProps} />
        {/* FeedbackLink sits inside the company row for CLOSED meetings */}
        <CardCompanyRow {...rowProps} showFeedback />
        <Flex gap={4} />
      </Box>
    );
  }

  if (myStatus === MEETING_REQUEST_STATUS.INCOMPLETE) {
    return (
      <Box {...sharedCardBox} bg={bg("#E9E5FF")}>
        <CardDateRow {...rowProps} />
        <CardCompanyRow {...rowProps} />
      </Box>
    );
  }

  return null;
};

export default MeetingCard;
